import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MobileLayout from "@/components/MobileLayout";
import { ChevronLeft, Plus, Dumbbell } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { getExerciseById, localizeExerciseName } from "@/lib/fitnessApi";
import { translateMuscle } from "@/lib/muscleGroupTranslations";
import { translateEquipment } from "@/lib/equipmentTranslations";
import { supabase } from "@/integrations/supabase/client";
import { useExerciseMedia } from "@/hooks/useExerciseMedia";
import FitnessDataState from "@/components/fitness/FitnessDataState";
import ExerciseDemonstration from "@/components/fitness/ExerciseDemonstration";
import ExerciseProgressHistory from "@/components/fitness/ExerciseProgressHistory";
import { fitnessExerciseProgressCopy } from "@/lib/fitnessExerciseProgressCopy";
import { toast } from "sonner";
import { useFitnessResource, notifyFitnessChanged } from "@/hooks/useFitnessResource";

const readIdentity = async () => true;

const ExerciseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { exerciseId } = useParams<{ exerciseId: string }>();
  const { t, lang } = useLanguage();
  const exercise = useMemo(() => getExerciseById(exerciseId || ""), [exerciseId]);
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState<"details" | "progress">("details");
  const progressCopy = fitnessExerciseProgressCopy(lang);
  useEffect(() => setView("details"), [exerciseId]);
  const media = useExerciseMedia(exercise?.name || "", exercise?.gifUrl);
  const identity = useFitnessResource(readIdentity);
  const selection = useRef({ id: exerciseId, epoch: 0 });
  if (selection.current.id !== exerciseId) selection.current = { id: exerciseId, epoch: selection.current.epoch + 1 };
  const pending = useRef<symbol | null>(null);
  useEffect(() => { pending.current = null; setAdding(false); }, [exerciseId, identity.userId]);

  const handleAddToWorkout = async () => {
    if (!exercise || pending.current || identity.status !== "ready" || !identity.userId) return;
    const ownerId = identity.userId;
    const target = exercise;
    const selected = selection.current;
    const ownerCurrent = identity.capture();
    const ticket = Symbol("add-exercise");
    const isCurrent = () => ownerCurrent() && selection.current === selected && pending.current === ticket;
    pending.current = ticket;
    setAdding(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (!isCurrent()) return;
      if (authError || user?.id !== ownerId) throw new Error("Fitness identity unavailable");
      const { data: plan, error: readError } = await supabase
        .from("workout_plans").select("id, days").eq("user_id", ownerId).eq("is_active", true).maybeSingle();
      if (!isCurrent()) return;
      if (readError) throw new Error("Fitness plan unavailable");
      if (!plan || !Array.isArray(plan.days) || !plan.days.length || !plan.days[0] || typeof plan.days[0] !== "object" || Array.isArray(plan.days[0])) {
        toast.error(t("fitness.library.noPlanActive")); return;
      }
      const firstDay = { ...plan.days[0] };
      firstDay.exercises = [...(Array.isArray(firstDay.exercises) ? firstDay.exercises : []), {
        name: target.name, sets: 3, reps: "10-12", rest: 90,
        muscle: target.target, gifUrl: target.gifUrl || "",
      }];
      const { data: { user: verifiedUser }, error: verifyError } = await supabase.auth.getUser();
      if (!isCurrent()) return;
      if (verifyError || verifiedUser?.id !== ownerId) throw new Error("Fitness identity changed");
      const { data: saved, error: saveError } = await supabase.from("workout_plans")
        .update({ days: [firstDay, ...plan.days.slice(1)] }).eq("id", plan.id).eq("user_id", ownerId).select("id").maybeSingle();
      if (!isCurrent()) return;
      if (saveError || saved?.id !== plan.id) throw new Error("Fitness plan update unconfirmed");
      toast.success(t("fitness.library.addedToWorkout"));
      notifyFitnessChanged();
    } catch {
      if (isCurrent()) { console.warn("[Fitness] exercise addition unconfirmed"); toast.error(t("fitness.library.addError")); }
    } finally {
      if (isCurrent()) { pending.current = null; setAdding(false); }
    }
  };


  if (!exercise) {
    return (
      <MobileLayout className="pb-28 pt-8">
        <button type="button" onClick={() => navigate(-1)} className="mb-6 flex min-h-11 items-center gap-2 rounded-xl px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <ChevronLeft size={18} aria-hidden="true" />{t("common.back")}
        </button>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Dumbbell size={36} aria-hidden="true" className="text-primary" />
          <p role="status" className="text-sm text-muted-foreground">{t("fitness.library.notFound")}</p>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout className="pb-28 pt-6">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <header>
          <button type="button" onClick={() => navigate(-1)} className="mb-4 inline-flex min-h-11 items-center gap-2 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <ChevronLeft size={18} aria-hidden="true" />{t("common.back")}
          </button>
          <p className="mb-2 text-xs font-display font-semibold uppercase tracking-wider text-primary">{translateMuscle(exercise.target, t)}</p>
          <h1 className="font-display text-3xl font-bold leading-tight text-foreground">{localizeExerciseName(exercise.name, t, lang)}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{translateEquipment(exercise.equipment, t)}</p>
        </header>

        <div className="flex gap-2" role="group" aria-label={progressCopy.title}>
          <button type="button" aria-pressed={view === "details"} onClick={() => setView("details")}
            className={"min-h-11 flex-1 rounded-xl border px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary " + (view === "details" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground")}>{progressCopy.details}</button>
          <button type="button" aria-pressed={view === "progress"} onClick={() => setView("progress")}
            className={"min-h-11 flex-1 rounded-xl border px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary " + (view === "progress" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground")}>{progressCopy.title}</button>
        </div>

        {view === "details" && <>
        <ExerciseDemonstration exerciseId={exercise.name} exerciseName={localizeExerciseName(exercise.name, t, lang)}
          media={media} loading={media.loading} error={media.error} onRetry={media.retry}
          instructions={exercise.instructions} />

        <section className="rounded-2xl border border-border bg-card p-5" aria-label={t("fitness.library.musclesWorked")}>
          <h2 className="mb-4 font-display text-lg font-semibold">{t("fitness.library.musclesWorked")}</h2>
          <dl className="space-y-4 text-sm">
            <div><dt className="mb-1 text-xs text-muted-foreground">{t("fitness.library.primaryMuscle")}</dt><dd className="font-medium text-primary">{translateMuscle(exercise.target, t)}</dd></div>
            {exercise.secondaryMuscles.length > 0 && <div><dt className="mb-2 text-xs text-muted-foreground">{t("fitness.library.secondaryMuscles")}</dt><dd className="flex flex-wrap gap-2">{exercise.secondaryMuscles.map(muscle => <span key={muscle} className="rounded-lg bg-background px-3 py-2">{translateMuscle(muscle, t)}</span>)}</dd></div>}
            <div><dt className="mb-1 text-xs text-muted-foreground">{t("fitness.library.equipmentLabel")}</dt><dd>{translateEquipment(exercise.equipment, t)}</dd></div>
          </dl>
        </section>

        </>}

        {view === "progress" && <ExerciseProgressHistory name={exercise.name} muscle={exercise.target} />}

        <FitnessDataState status={identity.status} busy={identity.isRefreshing} onRetry={identity.refresh} />
        <button type="button" onClick={handleAddToWorkout} disabled={adding || identity.status !== "ready"}
          className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-4 font-display text-base font-bold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-40">
          <Plus size={20} aria-hidden="true" />{t("fitness.library.addToWorkout")}
        </button>
      </div>
    </MobileLayout>
  );
};

export default ExerciseDetail;
